import { useState } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import {
  Video, Calendar, FileText, AlertTriangle, Phone,
  Clock, CheckCircle2, User, Ambulance, ArrowRight, Shield,
} from 'lucide-react';
import { toast } from 'sonner';

const DIMESSI = [
  {
    id: 1, nome: 'Vitale', cognome: 'Teresa', eta: 77,
    dimissione: '19/04/2026', giorniDomicilio: 1,
    diagnosi: 'Polmonite su BPCO — Ossigenoterapia domiciliare',
    cm: 'Inf. L. Conti', erogatore: 'Coop. Salute Roma',
    televisite: [
      { data: '21/04/2026', ora: '10:00', stato: 'programmata', note: 'Controllo parametri respiratori' },
      { data: '25/04/2026', ora: '11:30', stato: 'programmata', note: 'Follow-up terapia antibiotica' },
    ],
    referti: [
      { data: '20/04/2026', tipo: 'Parametri Vitali', esito: 'SpO2 94%, PA 130/78, FC 82. Stabile.', autore: 'Inf. M. Colombo' },
    ],
  },
  {
    id: 2, nome: 'Bianchi', cognome: 'Mario', eta: 74,
    dimissione: '20/04/2026', giorniDomicilio: 0,
    diagnosi: 'Ictus ischemico — Emiparesi dx — Riabilitazione',
    cm: 'Inf. A. Ferrara', erogatore: 'Coop. Riabilita Roma',
    televisite: [
      { data: '22/04/2026', ora: '09:00', stato: 'programmata', note: 'Valutazione neurologica post-dimissione' },
      { data: '27/04/2026', ora: '10:00', stato: 'programmata', note: 'Follow-up riabilitativo + Barthel Index' },
    ],
    referti: [],
  },
];

export default function OspedaleFollowup() {
  const [emergencyConfirm, setEmergencyConfirm] = useState(null);

  const handleEmergency = (pazId) => {
    setEmergencyConfirm(null);
    toast.error('Rientro urgente attivato', { description: 'Notifica inviata a Pronto Soccorso e COT. Preparare posto letto.' });
  };

  return (
    <AppLayout role="ospedale">
      <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6" data-testid="ospedale-followup">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Follow-up dimissioni</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Monitoraggio pazienti dimessi — Primi 14 giorni di assistenza domiciliare</p>
        </div>

        {/* Patients */}
        <div className="flex flex-col gap-12">
          {DIMESSI.map((pz, idx) => (
            <motion.div key={pz.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.08 }}>
              <Card className="border-0 shadow-sm rounded-2xl overflow-hidden" data-testid={`followup-card-${pz.id}`}>
                <CardContent className="p-0">
                  {/* Patient Header */}
                  <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/30 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/40 flex items-center justify-center text-sm font-bold text-blue-700 dark:text-blue-300">{pz.cognome[0]}{pz.nome[0]}</div>
                      <div>
                        <p className="text-sm font-bold text-slate-800 dark:text-white">{pz.cognome} {pz.nome}, {pz.eta} anni</p>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">{pz.diagnosi}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-0 text-[10px] font-bold">
                        Dim. {pz.dimissione}
                      </Badge>
                      <Badge className={`border-0 text-[10px] font-bold ${pz.giorniDomicilio <= 3 ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'}`}>
                        Giorno {pz.giorniDomicilio + 1}/14
                      </Badge>
                    </div>
                  </div>

                  <div className="p-6 space-y-5">
                    {/* Care Team */}
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><User className="w-3 h-3" /> CM: <strong className="text-slate-700 dark:text-slate-200">{pz.cm}</strong></span>
                      <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Erogatore: <strong className="text-slate-700 dark:text-slate-200">{pz.erogatore}</strong></span>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                      {/* Televisite programmate */}
                      <div className="flex flex-col gap-4" data-testid={`televisite-${pz.id}`}>
                        <h4 className="text-xs font-bold text-slate-800 dark:text-white flex items-center gap-2"><Video className="w-3.5 h-3.5 text-blue-500" />Televisite Programmate</h4>
                        {pz.televisite.map((tv, i) => (
                          <div key={i} className="flex items-center justify-between px-4 py-3 rounded-xl bg-blue-50/50 dark:bg-blue-950/10 border border-blue-100 dark:border-blue-800/30" data-testid={`televisita-${pz.id}-${i}`}>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                <Video className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div>
                                <p className="text-xs font-semibold text-slate-800 dark:text-white">{tv.data} — {tv.ora}</p>
                                <p className="text-[10px] text-slate-500">{tv.note}</p>
                              </div>
                            </div>
                            <Badge className="bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border-0 text-[9px] font-bold">
                              <Clock className="w-2.5 h-2.5 mr-1" />Programmata
                            </Badge>
                          </div>
                        ))}
                      </div>

                      {/* Referti dal territorio */}
                      <div className="space-y-3" data-testid={`referti-${pz.id}`}>
                        <h4 className="text-xs font-bold text-slate-800 dark:text-white flex items-center gap-2"><FileText className="w-3.5 h-3.5 text-teal-500" />Referti dal Territorio</h4>
                        {pz.referti.length === 0 ? (
                          <div className="px-4 py-6 rounded-xl bg-slate-50 dark:bg-slate-800/30 border border-slate-200 dark:border-slate-700 text-center">
                            <FileText className="w-6 h-6 text-slate-300 mx-auto mb-2" />
                            <p className="text-[10px] text-slate-400">Nessun referto disponibile. Il territorio caricherà i dati dopo il primo accesso.</p>
                          </div>
                        ) : (
                          pz.referti.map((r, i) => (
                            <div key={i} className="px-4 py-3 rounded-xl bg-teal-50/50 dark:bg-teal-950/10 border border-teal-100 dark:border-teal-800/30" data-testid={`referto-${pz.id}-${i}`}>
                              <div className="flex items-center justify-between mb-1">
                                <p className="text-xs font-semibold text-slate-800 dark:text-white">{r.tipo}</p>
                                <span className="text-[10px] text-slate-400">{r.data}</span>
                              </div>
                              <p className="text-[11px] text-slate-600 dark:text-slate-300">{r.esito}</p>
                              <p className="text-[10px] text-slate-400 mt-1">{r.autore}</p>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Emergency CTA */}
                    <div className="pt-3 border-t border-slate-100 dark:border-slate-800">
                      {emergencyConfirm === pz.id ? (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center justify-between px-5 py-4 rounded-xl bg-red-50 dark:bg-red-950/20 border-2 border-red-300 dark:border-red-700" data-testid={`emergency-confirm-${pz.id}`}>
                          <div>
                            <p className="text-sm font-bold text-red-800 dark:text-red-300">Confermi il rientro urgente?</p>
                            <p className="text-[10px] text-red-600/80 dark:text-red-400/80 mt-0.5">Notifica automatica a PS, COT e Reparto di provenienza.</p>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" className="rounded-lg text-xs" onClick={() => setEmergencyConfirm(null)} data-testid={`cancel-emergency-${pz.id}`}>Annulla</Button>
                            <Button size="sm" className="rounded-lg text-xs bg-red-600 hover:bg-red-700 text-white gap-1.5" onClick={() => handleEmergency(pz.id)} data-testid={`confirm-emergency-${pz.id}`}>
                              <Ambulance className="w-3.5 h-3.5" /> Conferma Rientro
                            </Button>
                          </div>
                        </motion.div>
                      ) : (
                        <Button
                          variant="outline"
                          className="gap-2 rounded-xl text-xs font-semibold border-2 border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 h-10"
                          onClick={() => setEmergencyConfirm(pz.id)}
                          data-testid={`btn-emergency-${pz.id}`}
                        >
                          <Ambulance className="w-4 h-4" /> Facilita Rientro / Ricovero
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </AppLayout>
  );
}
